import * as React from 'react';
import { useState, useEffect } from 'react';
import { logout, getUserApi, deleteAccount } from '../api/api';
import { useNavigate } from 'react-router-dom';
import AlertAccent from '../components/AlertAccent';
import BtnFilled from '../components/BtnFilled';
import Modal from '../components/Modal';
import { useUserStore } from '../stores';
import AccountVerification from '../features/User/AccountVerification';
import EditAccountForm from '../features/User/EditAccountForm';
import EmailAlert from '../features/User/EmailAlert';

const User = () => {
  const navigate = useNavigate();
  const { user, setUser, setAuthorized } = useUserStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [countdownActive, setCountdownActive] = useState(false);

  const tableData = [
    { title: 'ФИO', value: user?.fio },
    {
      title: 'Ваш email',
      value: user?.email,
    },
    { title: 'Телефон', value: user?.phone },
    // { title: 'Роль', value: user?.role },
    {
      title: 'Статус',
      value: user?.verified_at ? 'Подтвержден' : 'Не подтвержден',
    },
    { title: 'Версия web', value: `${process.env.REACT_APP_VER} (${process.env.REACT_APP_MODE})` },
  ];

  const out = async () => {
    await logout();
    await getUserApi(
      (data) => {
        setUser(data);
        setAuthorized(true);
      },
      (err) => {
        setAuthorized(false);
      }
    );
    navigate('/');
  };

  // Функция, вызываемая при нажатии кнопки "Удалить аккаунт"
  const del = () => {
    setShowDeleteModal(true);
  };

  // Функция, которая запускается при нажатии кнопки подтверждения в модальном окне
  const handleDeleteAccount = () => {
    // Запускаем обратный отсчёт
    setCountdownActive(true);
    setCountdown(30);
  };

  useEffect(() => {
    let interval;
    if (countdownActive && countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdownActive && countdown === 0) {
      deleteAccount(
        (data) => {
          setDeleteSuccess(true);
          setCountdownActive(false);
          setAuthorized(false);
        },
        (err) => {
          setDeleteError(`Ошибка удаления аккаунта: ${err?.message || 'Неизвестная ошибка'}`);
          setCountdownActive(false);
        }
      );
    }
    return () => clearInterval(interval);
  }, [countdownActive, countdown]);

  // После успешного удаления показываем сообщение и через 3 секунды перенаправляем пользователя
  useEffect(() => {
    let timeout;
    if (deleteSuccess) {
      timeout = setTimeout(() => {
        window.location.href = window.location.origin;
      }, 10000);
    }
    return () => clearTimeout(timeout);
  }, [deleteSuccess, navigate]);

  // Функция для закрытия модального окна, сброса отсчёта и отмены удаления
  const closeModal = () => {
    setShowDeleteModal(false);
    setDeleteError('');
    setDeleteSuccess(false);
    setCountdownActive(false);
    setCountdown(30);
  };

  return (
    <div className="flex w-full min-h-screen p-4 items-start justify-center bg-gradient-to-r from-[#ededed] to-[#dcdcdc]">
      <div className="relative w-full max-w-lg">
        <EditAccountForm />

        <form className="bg-white shadow-2xl rounded-3xl px-8 pt-6 pb-8 mb-4">
          <div>
            <AccountVerification />
            <table className="table-auto w-full text-base my-4">
              <tbody>
                {tableData.map((item) => {
                  return (
                    item.value && (
                      <tr key={item.title} className="md:table-row flex flex-col md:border-b">
                        <td className="px-2 py-0.5 sm:table-cell font-semibold">{item.title}:</td>
                        <td className="px-2 py-0.5 sm:table-cell break-all">{item.value}</td>
                      </tr>
                    )
                  );
                })}
              </tbody>
            </table>
            <EmailAlert isForeignMail={user?.is_foreign_email} />

            <div className="flex justify-center items-center gap-4 flex-wrap md:flex-nowrap">
              <BtnFilled title="Удалить аккаунт" btnCb={del} />
              <BtnFilled title="Выход" btnCb={out} />
            </div>
          </div>
        </form>
      </div>

      {/* Модальное окно для подтверждения удаления аккаунта */}
      {showDeleteModal && (
        <Modal
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          // Если возникла ошибка, вместо кнопки "Удалить" показываем "Закрыть"
          titleOn={deleteError ? 'Закрыть' : countdownActive ? 'Отмена' : 'Удалить'}
          saveCb={deleteError ? closeModal : countdownActive ? closeModal : handleDeleteAccount}
          // Не показываем кнопку "Удалить", если ошибка произошла или удаление прошло успешно
          showSave={!deleteSuccess && !deleteError}
          closeCb={closeModal}
          // Если ошибка – меняем подпись на кнопке отмены на "Закрыть"
          cancelTitle={deleteError ? 'Закрыть' : 'Отмена'}
          title="Вы действительно хотите удалить аккаунт?">
          {deleteSuccess ? (
            <AlertAccent
              title="Аккаунт успешно удалён"
              text="Удалены все данные, включая персональную информацию, историю действий и настройки профиля."
              showAlert={true}
            />
          ) : deleteError ? (
            <AlertAccent title="Ошибка удаления аккаунта" text={deleteError} showAlert={true} />
          ) : countdownActive ? (
            <p>Ваш аккаунт будет удалён через {countdown} секунд.</p>
          ) : (
            <p>После подтверждения удаления аккаунта запустится обратный отсчёт в 30 секунд, по окончании которого аккаунт будет удалён.</p>
          )}
        </Modal>
      )}
    </div>
  );
};

export default User;
